import { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ForumIcon from "@mui/icons-material/Forum";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SearchIcon from "@mui/icons-material/Search";

import AppCard from "@plannel/components/ui/AppCard";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import versionNoteService from "@plannel/services/system/version-note-service";
import CodeUtil from "@plannel/pages/utils/code-util";
import debug from "@plannel/utils/debug-util";
import dateUtils, { DATETIME_MINUTE_FORMAT } from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

/*
 * S&OP › Version Notes — 모듈과 버전을 가로질러 셀 노트를 모아 본다.
 *
 * 노트는 DP 는 z_dp_note, RP · MP 는 z_note 에 셀 단위로 남는다. 한 셀에 여러 건(seq_num)이
 * 쌓이므로 목록은 "셀(버전 + note_key) 하나 = 한 줄" 로 묶고, 오른쪽에 그 셀의 노트를 시간순으로 보여 준다.
 * 이 화면은 읽기 전용이다 — 쓰기는 각 모듈 화면의 노트 패널이 한다(Open 으로 이동).
 */

const SPLITTER = "#SPLIT#";

/*
 * 모듈별 note_key 조각. 순서는 CodeUtil.getNoteKey 와 같아야 한다.
 *   DP  customerHrchyCd # itemHrchyCd # baseDate # measureCd
 *   RP  originLocCd # destLocCd # itemCd # baseDate # measure
 *   MP  locCd(공장) # itemCd # baseDate # measure
 * RP 의 로케이션 수준 노트(RP Review 의 loc-item 표식)는 날짜 · measure 가 없다 — 빈 조각은 "—" 로 둔다.
 */
const KEY_PARTS = {
  DP: ["customer", "item", "time", "measure"],
  RP: ["originLocation", "destLocation", "item", "time", "measure"],
  MP: ["plant", "item", "time", "measure"],
};

/*
 * 이동할 화면(TabMenuList key).
 *   DP      항상 DP Workbench (Demand Netting 에서 남긴 Netting_… 노트도 셀을 보는 곳은 Workbench 다)
 *   RP · MP 버전이 릴리즈됐으면 Review, 아니면 PSI Simulation(아직 손보는 중인 버전을 다루는 화면)
 */
const DP_WORKBENCH_MENU = { key: 3102, title: "menuDpPractitionerWorkbench" };
const TARGET_MENU = {
  RP: { review: { key: 2101, title: "menuRpReview" }, psi: { key: 2102, title: "menuRpPsiSimulation" } },
  MP: { review: { key: 4101, title: "menuMpReview" }, psi: { key: 4102, title: "menuMpPsiSimulation" } },
};

/* 모듈 칩 색 — 연한 바탕 + 진한 글자(대비 4.5:1 이상) */
const MODULE_CHIP = {
  DP: { bgcolor: "#d6ebf0", color: "#025B6E" },
  RP: { bgcolor: "#f6e6cf", color: "#7a4a06" },
  MP: { bgcolor: "#e7e2f3", color: "#4a3a7a" },
};

const PERIODS = [
  { value: 7, label: "versionNotesLast7Days" },
  { value: 30, label: "versionNotesLast30Days" },
  { value: 90, label: "versionNotesLast90Days" },
  { value: 0, label: "versionNotesAllPeriod" },
];

const ALL = "ALL";

/* 목록 열 — 헤더와 줄이 같은 값을 써야 칸이 맞는다. 마지막(Open 버튼)만 줄 안의 선택 버튼 밖에 있다 */
const CELL_COLUMNS = ["56px", "minmax(120px, 170px)", "minmax(110px, 140px)", "minmax(0, 1fr)", "56px", "minmax(110px, 140px)"];
const OPEN_COLUMN = "84px";
const ROW_COLUMNS = [...CELL_COLUMNS, OPEN_COLUMN].join(" ");

const ModuleChip = ({ moduleCd }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center", height: 20, px: "7px", borderRadius: "4px",
    fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: "0.04em", ...MODULE_CHIP[moduleCd],
  }}>
    {moduleCd}
  </Box>
);

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const getTargetMenu = (thread) => {
  if (thread.moduleCd === "DP") {
    return DP_WORKBENCH_MENU;
  }
  const menus = TARGET_MENU[thread.moduleCd];
  if (!menus) {
    return null;
  }
  return thread.versionStatusCd === CodeUtil.versionStatusCode.RELEASED ? menus.review : menus.psi;
};

/* 상세 패널에 내는 이동 버튼들 — 기본 화면이 앞. RP · MP 는 Review 와 PSI 둘 다 낸다 */
const getTargetMenus = (thread) => {
  const main = getTargetMenu(thread);
  const menus = TARGET_MENU[thread.moduleCd];
  if (!main || !menus) {
    return main ? [main] : [];
  }
  return [main, main === menus.review ? menus.psi : menus.review];
};

/* 노트 여러 건 → 셀 단위 스레드. 입력은 최근 수정 순이므로 첫 건이 최신이다 */
const toThreads = (notes) => {
  const byCell = new Map();
  for (const note of notes) {
    const cellKey = `${note.planVersionId}|${note.noteKey}`;
    if (!byCell.has(cellKey)) {
      const parts = String(note.noteKey ?? "").split(SPLITTER);
      const labels = KEY_PARTS[note.moduleCd] ?? [];
      const keyParts = labels.map((label, i) => ({ label, value: parts[i] || "" }));
      const byLabel = Object.fromEntries(keyParts.map(p => [p.label, p.value]));
      byCell.set(cellKey, {
        cellKey,
        moduleCd: note.moduleCd,
        planVersionId: note.planVersionId,
        planVersionName: note.planVersionName,
        versionStatusCd: note.versionStatusCd,
        keyParts,
        bucket: byLabel.time,
        measure: byLabel.measure,
        target: keyParts.filter(p => p.label !== "time" && p.label !== "measure").map(p => p.value).filter(Boolean),
        latest: note,
        entries: [],
      });
    }
    byCell.get(cellKey).entries.push(note);
  }
  const threads = [...byCell.values()];
  // 스레드 안은 오래된 것 → 최신(대화처럼 읽힌다)
  threads.forEach(th => th.entries.sort((a, b) => a.seqNum - b.seqNum));
  return threads;
};

const VersionNotes = ({ t }) => {
  const { isPermitted } = useMenuAccess();

  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleCd, setModuleCd] = useState(ALL);
  const [versionId, setVersionId] = useState(ALL);
  const [period, setPeriod] = useState(30);
  const [keyword, setKeyword] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);

  const retrieve = useCallback(() => {
    setLoading(true);
    const updatedFrom = period > 0 ? new Date(Date.now() - period * 24 * 60 * 60 * 1000) : null;
    versionNoteService.list({ updatedFrom })
      .then(res => {
        setNotes(res.data?.result ?? []);
        setModules(res.data?.moduleCds ?? []);
      })
      .catch(e => {
        debug(e);
        setNotes([]);
      })
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { retrieve(); }, [retrieve]);

  const threads = useMemo(() => toThreads(notes), [notes]);

  const moduleThreads = useMemo(
    () => threads.filter(th => moduleCd === ALL || th.moduleCd === moduleCd),
    [threads, moduleCd]);

  const shown = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return moduleThreads.filter(th => {
      if (versionId !== ALL && th.planVersionId !== versionId) return false;
      if (!q) return true;
      const hay = [
        th.planVersionName, ...th.keyParts.map(p => p.value),
        ...th.entries.flatMap(e => [e.contentTxt, e.createdUser, e.locNm]),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [moduleThreads, versionId, keyword]);

  /* 왼쪽 버전 목록 — 모듈별 묶음, 각 버전의 셀(스레드) 수 */
  const versionGroups = useMemo(() => {
    const visibleModules = moduleCd === ALL ? modules : modules.filter(m => m === moduleCd);
    return visibleModules.map(m => {
      const versions = new Map();
      threads.filter(th => th.moduleCd === m).forEach(th => {
        const v = versions.get(th.planVersionId) ?? {
          id: th.planVersionId, name: th.planVersionName, statusCd: th.versionStatusCd, count: 0,
        };
        v.count += 1;
        versions.set(th.planVersionId, v);
      });
      return { moduleCd: m, versions: [...versions.values()].sort((a, b) => String(b.name).localeCompare(String(a.name))) };
    });
  }, [threads, modules, moduleCd]);

  const selected = shown.find(th => th.cellKey === selectedKey) ?? shown[0] ?? null;

  const handleModule = (value) => {
    setModuleCd(value);
    setVersionId(ALL);
  };

  const openMenu = (menu) => {
    window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: menu.key } }));
  };

  const moduleOptions = [
    { value: ALL, label: t("all") },
    ...modules.map(m => ({ value: m, label: m })),
  ];

  const labelOf = (label) => t(label);
  /* RP · MP 의 measure 코드(demandQty 등)는 번역 키이기도 하다. 키가 없는 코드(DP 의 SF_QTY 등)는 그대로 */
  const measureOf = (measure) => (measure ? t(measure, { defaultValue: measure }) : "—");
  const partValueOf = (p) => (p.label === "measure" ? measureOf(p.value) : p.value || "—");
  const formatTs = (ts) => (ts ? dateUtils.formatDate(ts, DATETIME_MINUTE_FORMAT) : "");

  const versionButtonSx = (active) => ({
    width: "100%", minHeight: 44, px: "10px", py: "6px", borderRadius: "6px",
    display: "flex", alignItems: "center", gap: "10px", textAlign: "left",
    bgcolor: active ? "rgba(4,139,168,0.12)" : "transparent",
    boxShadow: active ? "inset 0 0 0 1px rgba(4,139,168,0.35)" : "none",
    "&:hover": { bgcolor: active ? "rgba(4,139,168,0.16)" : "rgba(4,139,168,0.06)" },
  });

  // 대상 화면 권한이 없으면 버튼을 내지 않는다(사이드바와 같은 규칙)
  const renderOpenButton = (thread) => {
    const menu = getTargetMenu(thread);
    if (!menu || !isPermitted(menu.key)) return null;
    return (
      <Tooltip title={t("versionNotesOpenIn", { screen: t(menu.title) })}>
        <Button size="small" variant="outlined" endIcon={<NorthEastIcon sx={{ fontSize: 14 }} />}
          onClick={() => openMenu(menu)}
          sx={{ minWidth: 0, px: 1, textTransform: "none", fontSize: FS.SUB }}>
          {t("versionNotesOpen")}
        </Button>
      </Tooltip>
    );
  };

  /* 상세 패널 — 기본 화면은 채운 버튼, 나머지(RP · MP 의 다른 화면)는 테두리 버튼 */
  const renderOpenButtons = (thread) => {
    const menus = getTargetMenus(thread).filter(m => isPermitted(m.key));
    if (menus.length === 0) return null;
    return (
      // 세로로 쌓는다 — 패널 폭(380)에 "Open in MP PSI Simulation" 두 개를 나란히 두면 줄이 넘친다
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {menus.map((menu, i) => (
          <Button key={menu.key} fullWidth variant={i === 0 ? "contained" : "outlined"} endIcon={<NorthEastIcon />}
            onClick={() => openMenu(menu)} sx={{ textTransform: "none" }}>
            {t("versionNotesOpenIn", { screen: t(menu.title) })}
          </Button>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0 }}>
      <FilterContainer sx={{ columnGap: 1.5 }}>
        <SegmentedControl value={moduleCd} onChange={handleModule} options={moduleOptions} />
        <TextField
          size="small"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder={t("versionNotesSearch")}
          inputProps={{ "aria-label": t("search") }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 320 }}
        />
        <Select size="small" value={period} onChange={e => setPeriod(e.target.value)} sx={{ width: 170 }}
          inputProps={{ "aria-label": t("period") }}>
          {PERIODS.map(p => <MenuItem key={p.value} value={p.value}>{t(p.label)}</MenuItem>)}
        </Select>
        <Box sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
          {t("versionNotesCount", { cells: shown.length, notes: shown.reduce((a, th) => a + th.entries.length, 0) })}
        </Box>
      </FilterContainer>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2 }}>
        {/* 버전 */}
        <AppCard component="nav" aria-label={t("version")}
          sx={{ width: 260, flex: "none", p: 1.5, display: "flex", flexDirection: "column", gap: 0.5, overflow: "auto" }}>
          <ButtonBase onClick={() => setVersionId(ALL)} sx={versionButtonSx(versionId === ALL)}>
            <Box sx={{ flex: 1, fontSize: FS.TITLE, fontWeight: 500 }}>{t("versionNotesAllVersions")}</Box>
            <Box sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{moduleThreads.length}</Box>
          </ButtonBase>
          {versionGroups.map(g => (
            <Box key={g.moduleCd} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ mt: 1.5, px: "10px", pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                <ModuleChip moduleCd={g.moduleCd} />
              </Box>
              {g.versions.length === 0 && (
                <Box sx={{ px: "10px", fontSize: FS.SUB, color: "text.secondary" }}>{t("noData")}</Box>
              )}
              {g.versions.map(v => (
                <ButtonBase key={v.id} onClick={() => setVersionId(v.id)} sx={versionButtonSx(versionId === v.id)}>
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                    <Box sx={{ fontSize: FS.TITLE, fontWeight: 500, ...ellipsis }}>{v.name}</Box>
                    <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{CodeUtil.versionStatusLabel(v.statusCd)}</Box>
                  </Box>
                  <Box sx={{
                    minWidth: 22, height: 20, px: "6px", borderRadius: "10px", bgcolor: "grey.200",
                    fontSize: FS.SUB, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {v.count}
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          ))}
        </AppCard>

        {/* 셀 목록 */}
        <AppCard sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{
            display: "grid", gridTemplateColumns: ROW_COLUMNS, gap: 1.5, px: 2, py: 1.25,
            borderBottom: 1, borderColor: "divider", bgcolor: "grey.50",
            fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary",
          }}>
            <Box>{t("module")}</Box>
            <Box>{t("version")}</Box>
            <Box>{t("versionNotesCell")}</Box>
            <Box>{t("versionNotesLatest")}</Box>
            <Box>{t("versionNotesEntries")}</Box>
            <Box>{t("updatedTs")}</Box>
            <Box />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {loading && [0, 1, 2, 3, 4].map(i => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ m: 1.5, borderRadius: 1 }} />
            ))}
            {!loading && shown.map(th => {
              const active = selected?.cellKey === th.cellKey;
              return (
                <Box key={th.cellKey} sx={{
                  display: "grid", gridTemplateColumns: `minmax(0, 1fr) ${OPEN_COLUMN}`, gap: 1.5, alignItems: "center",
                  px: 2, py: 1.5, borderBottom: 1, borderColor: "grey.100",
                  bgcolor: active ? "rgba(4,139,168,0.08)" : "transparent",
                  boxShadow: active ? "inset 3px 0 0 #048BA8" : "none",
                  "&:hover": { bgcolor: active ? "rgba(4,139,168,0.1)" : "grey.50" },
                }}>
                  <ButtonBase onClick={() => setSelectedKey(th.cellKey)} sx={{
                    display: "grid", gridTemplateColumns: CELL_COLUMNS.join(" "),
                    gap: 1.5, alignItems: "center", textAlign: "left", fontSize: FS.TITLE, minWidth: 0,
                  }}>
                    <Box><ModuleChip moduleCd={th.moduleCd} /></Box>
                    <Box sx={{ minWidth: 0, fontWeight: 500, ...ellipsis }}>{th.planVersionName}</Box>
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                      <Box sx={{ fontSize: FS.SUB, fontFamily: "monospace", ...ellipsis }}>{th.bucket || "—"}</Box>
                      <Box sx={{ fontSize: FS.SUB, color: "text.secondary", ...ellipsis }}>{measureOf(th.measure)}</Box>
                    </Box>
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                      <Box sx={{ fontSize: FS.SUB, color: "text.secondary", ...ellipsis }}>
                        {th.target.join(th.moduleCd === "RP" ? " → " : " · ")}
                      </Box>
                      <Box sx={{
                        lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere",
                      }}>
                        {th.latest.contentTxt}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                      {th.entries.length}
                    </Box>
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                      <Box sx={{ fontSize: FS.SUB, ...ellipsis }}>{formatTs(th.latest.updatedTs)}</Box>
                      <Box sx={{ fontSize: FS.SUB, color: "text.secondary", ...ellipsis }}>
                        {th.latest.updatedUser || th.latest.createdUser}
                      </Box>
                    </Box>
                  </ButtonBase>
                  <Box>{renderOpenButton(th)}</Box>
                </Box>
              );
            })}
            {!loading && shown.length === 0 && (
              <Box sx={{ py: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, color: "text.secondary" }}>
                <ForumIcon sx={{ fontSize: 32, color: "grey.400" }} />
                <Box sx={{ fontSize: FS.TITLE, fontWeight: 500, color: "text.primary" }}>{t("versionNotesEmpty")}</Box>
              </Box>
            )}
          </Box>
        </AppCard>

        {/* 선택한 셀 */}
        <AppCard component="aside" aria-label={t("notes")}
          sx={{ width: 380, flex: "none", display: "flex", flexDirection: "column" }}>
          {selected ? (
            <>
              <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ModuleChip moduleCd={selected.moduleCd} />
                  <Box sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{CodeUtil.versionStatusLabel(selected.versionStatusCd)}</Box>
                </Box>
                <Box sx={{ fontSize: FS.H2, fontWeight: 600 }}>{selected.planVersionName}</Box>
                <Box component="dl" sx={{
                  m: 0, display: "grid", gridTemplateColumns: "110px minmax(0, 1fr)", rowGap: 0.75, columnGap: 1.25,
                  fontSize: FS.SUB,
                }}>
                  {selected.keyParts.map(p => (
                    <Box key={p.label} sx={{ display: "contents" }}>
                      <Box component="dt" sx={{ color: "text.secondary" }}>{labelOf(p.label)}</Box>
                      <Box component="dd" sx={{ m: 0, fontFamily: "monospace", overflowWrap: "anywhere" }}>{partValueOf(p)}</Box>
                    </Box>
                  ))}
                </Box>
                {renderOpenButtons(selected)}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.75 }}>
                <Box sx={{ fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary" }}>
                  {t("versionNotesEntries")} · {selected.entries.length}
                </Box>
                {selected.entries.map(e => (
                  <Box key={e.id} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", fontSize: FS.SUB }}>
                      <Box sx={{ fontWeight: 600 }}>{e.createdUser}</Box>
                      <Box sx={{ color: "text.secondary" }}>{formatTs(e.updatedTs)}</Box>
                      <Box sx={{ flex: 1 }} />
                      <Box sx={{ color: "text.secondary", fontFamily: "monospace" }}>#{e.seqNum}</Box>
                    </Box>
                    <Box sx={{
                      fontSize: FS.TITLE, lineHeight: 1.5, p: "10px 12px", borderRadius: "8px",
                      border: 1, borderColor: "divider", whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                    }}>
                      {e.contentTxt}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", fontSize: FS.TITLE }}>
              {loading ? <Skeleton variant="rectangular" width="80%" height={120} /> : t("versionNotesEmpty")}
            </Box>
          )}
        </AppCard>
      </Box>
    </Box>
  );
};

export default withTranslation()(VersionNotes);
